/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type PayrollRun, type PayrollStatus } from '../../lib/api/payroll.js';
import { useTherapists } from '../../lib/queries/clinical.js';
import { usePayrollRuns } from '../../lib/queries/payroll.js';
import { cn } from '../../lib/utils/cn.js';
import { formatInr } from '../../lib/utils/money.js';

import { PayrollRoster } from './payroll-roster.js';
import { PayrollRunForm } from './payroll-run-form.js';
import { RunDetailPanel } from './run-detail-panel.js';

/** A gradient summary tile (Figma payroll headline metrics). */
function SummaryCard({
  label,
  value,
  gradient,
}: {
  readonly label: string;
  readonly value: string;
  readonly gradient: string;
}): JSX.Element {
  return (
    <div className={cn('rounded-2xl bg-gradient-to-br p-5 text-white', gradient)}>
      <p className="text-sm text-white/80">{label}</p>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </div>
  );
}

/** Pending / Paid / Total-therapists tiles, summed across runs (Figma). */
function PayrollSummary({ runs }: { readonly runs: readonly PayrollRun[] }): JSX.Element {
  const { t } = useTranslation();
  const therapists = useTherapists();
  const pendingPaise = runs
    .filter((r) => r.status !== 'paid')
    .reduce((sum, r) => sum + r.totalPaise, 0);
  const paidPaise = runs
    .filter((r) => r.status === 'paid')
    .reduce((sum, r) => sum + r.totalPaise, 0);
  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-3">
      <SummaryCard
        label={t('payroll.pendingPayroll')}
        value={formatInr(pendingPaise)}
        gradient="from-amber-500 to-orange-500"
      />
      <SummaryCard
        label={t('payroll.paidThisPeriod')}
        value={formatInr(paidPaise)}
        gradient="from-emerald-500 to-emerald-600"
      />
      <SummaryCard
        label={t('payroll.totalTherapists')}
        value={String((therapists.data ?? []).length)}
        gradient="from-violet-500 to-purple-600"
      />
    </div>
  );
}

const STATUS_TONE: Readonly<Record<PayrollStatus, BadgeTone>> = {
  draft: 'slate',
  processed: 'violet',
  paid: 'green',
};

interface RunRowProps {
  readonly run: PayrollRun;
  readonly selected: boolean;
  readonly onSelect: (id: string) => void;
}

function RunRow({ run, selected, onSelect }: RunRowProps): JSX.Element {
  const { t } = useTranslation();
  const handleSelect = useCallback(() => {
    onSelect(run.id);
  }, [onSelect, run.id]);

  return (
    <tr
      onClick={handleSelect}
      className={cn(
        'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60',
        selected && 'bg-[var(--accent-soft)]',
      )}
    >
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">{run.periodLabel}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{formatInr(run.totalPaise)}</td>
      <td className="px-5 py-3">
        <Badge label={t(`payrollStatus.${run.status}`)} tone={STATUS_TONE[run.status]} />
      </td>
    </tr>
  );
}

/** Finance → Payroll (Figma). Runs list + selected-run payslips. */
export function FinancePayrollPage(): JSX.Element {
  const { t } = useTranslation();
  const runs = usePayrollRuns();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div className="p-8">
      <PageHeader
        title={t('payroll.title')}
        subtitle={t('payroll.count', { count: runs.data?.length ?? 0 })}
        action={
          <Button onClick={openModal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('payroll.add')}
          </Button>
        }
      />

      <PayrollRoster />

      <h2 className="mb-4 mt-8 text-lg font-semibold text-slate-800 dark:text-white">
        {t('payroll.runs')}
      </h2>
      <PayrollSummary runs={runs.data ?? []} />

      <div className="grid gap-6 lg:grid-cols-2">
        <QueryState
          isLoading={runs.isLoading}
          isError={runs.isError}
          isEmpty={(runs.data ?? []).length === 0}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('payroll.empty')}
        >
          <div className="h-fit overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 font-semibold">{t('payroll.colPeriod')}</th>
                  <th className="px-5 py-3 font-semibold">{t('payroll.colTotal')}</th>
                  <th className="px-5 py-3 font-semibold">{t('payroll.colStatus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {(runs.data ?? []).map((run) => (
                  <RunRow
                    key={run.id}
                    run={run}
                    selected={selectedId === run.id}
                    onSelect={setSelectedId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>

        {selectedId !== undefined && <RunDetailPanel runId={selectedId} />}
      </div>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('payroll.add')}
        description={t('payroll.addSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <PayrollRunForm onDone={closeModal} />
      </Modal>
    </div>
  );
}
